import React, {useState, useEffect} from "react"
import * as Yup from "yup"
import {Formik} from "formik"
import TextField from "../../../Address/Textfield"
import Card from "../../../../containers/Card/Card"
import AddressComponent from "../../../Address/Address"
import {US_COUNTRY_CODE} from "../../../../resources/address"
import {
	BusinessAnnualRevenue,
	BusinessNumberOfEmployees,
	BusinessVertical,
	CashFlow,
	IndustryType,
} from "../../../../resources/application"
import {Address, BusinessContact, Phone, ErrorDocument} from "../../../../resources/common"
import LeftRightSlots from "../../../../containers/LeftRightSlots/LeftRightSlots"
import PhoneNumber from "../../../Address/PhoneNumber"
import Dropdown from "../../../Address/Dropdown"
import CheckboxField from "../../../CheckboxField/CheckboxField"
import CountriesOfOperationFormField from "../../../ReevaluateApplication/Forms/CountriesOfOperationFormField"
import reevaluateBusinessProductApplicationValidationSchema from "./reevaluateBusinessProductApplicationValidationSchema"

export interface ReevaluatedBusinessProductApplicationData {
	name: string
	dba?: string
	address: Address
	operatingAddress?: Address
	ein: string
	phone: Phone
	stateOfIncorporation: string
	businessVertical?: BusinessVertical | ""
	yearOfIncorporation?: string
	website?: string | null
	numberOfEmployees?: BusinessNumberOfEmployees | ""
	annualRevenue?: BusinessAnnualRevenue | ""
	cashFlow?: CashFlow | ""
	industry?: IndustryType | ""
	countriesOfOperation?: string[]
	contact?: BusinessContact
}

const validationSchema = Yup.object().shape(reevaluateBusinessProductApplicationValidationSchema)

export default function ReevaluateBusinessProductApplicationForm(props: {
	businessApplication: ReevaluatedBusinessProductApplicationData
	submitCallback: (values: ReevaluatedBusinessProductApplicationData) => void
	formId: string
	error?: ErrorDocument
	onErrorDismiss: () => void
	disableSensitiveFields?: boolean
}) {
	const {businessApplication, submitCallback, formId, error, onErrorDismiss, disableSensitiveFields = false} = props
	const [hasUserInteractedWithOperatingAddress, setHasUserInteractedWithOperatingAddress] = useState(false)

	useEffect(() => {
		if (error) {
			setHasUserInteractedWithOperatingAddress(false)
		}
	}, [error])

	const initialFormValues = {
		address: {
			street: businessApplication.address?.street,
			street2: businessApplication.address?.street2,
			city: businessApplication.address?.city,
			state: businessApplication.address?.state ?? "",
			postalCode: businessApplication.address?.postalCode,
			country: businessApplication.address?.country ?? US_COUNTRY_CODE,
		},
		ein: businessApplication.ein,
		phone: businessApplication.phone,
		name: businessApplication.name,
		website: businessApplication.website,
		dba: businessApplication.dba,
		stateOfIncorporation: businessApplication.stateOfIncorporation,
		businessVertical: businessApplication.businessVertical,
		yearOfIncorporation: businessApplication.yearOfIncorporation,
		numberOfEmployees: businessApplication.numberOfEmployees,
		annualRevenue: businessApplication.annualRevenue,
		cashFlow: businessApplication.cashFlow,
		operatingAddress: {
			street: businessApplication.operatingAddress?.street ?? "",
			street2: businessApplication.operatingAddress?.street2 ?? "",
			city: businessApplication.operatingAddress?.city ?? "",
			country: businessApplication.operatingAddress?.country ?? "US",
			state: businessApplication.operatingAddress?.state ?? "CA",
			postalCode: businessApplication.operatingAddress?.postalCode ?? "",
		},
		industry: businessApplication.industry,
		countriesOfOperation: businessApplication.countriesOfOperation,
	}

	const title = `Business - ${businessApplication.name}`

	const errorTitle = error?.errors?.[0]?.title
	const isOperatingAddressError = errorTitle?.includes("operatingAddress is required") ?? false
	const operatingAddressError = errorTitle?.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()
	const shouldShowAddressError = isOperatingAddressError && !hasUserInteractedWithOperatingAddress

	const handleOperatingAddressInteraction = () => {
		if (!hasUserInteractedWithOperatingAddress) {
			setHasUserInteractedWithOperatingAddress(true)
			onErrorDismiss()
		}
	}

	return (
		<Card className="reevaluate-form" title={title}>
			<Formik
				initialValues={initialFormValues}
				validationSchema={validationSchema}
				onSubmit={(values) => {
					const isOperatingAddressEmpty =
						!values.operatingAddress?.street?.trim() &&
						!values.operatingAddress?.street2?.trim() &&
						!values.operatingAddress?.city?.trim() &&
						!values.operatingAddress?.postalCode?.trim()

					submitCallback({
						...values,
						numberOfEmployees: values.numberOfEmployees === "" ? undefined : values.numberOfEmployees,
						annualRevenue: values.annualRevenue === "" ? undefined : values.annualRevenue,
						cashFlow: values.cashFlow === "" ? undefined : values.cashFlow,
						operatingAddress: isOperatingAddressEmpty ? undefined : values.operatingAddress,
						businessVertical: values.businessVertical === "" ? undefined : values.businessVertical,
						industry: values.industry === "" ? undefined : values.industry,
						countriesOfOperation: values.countriesOfOperation?.length ? values.countriesOfOperation : undefined,
					})
				}}
			>
				{({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => {
					return (
						<form noValidate onSubmit={handleSubmit} id={formId}>
							<LeftRightSlots
								left={
									<>
										<TextField
											label="Business Name"
											placeholder="Enter Business Name"
											name="name"
											value={values.name}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>

										<TextField
											label="EIN"
											placeholder="Enter EIN"
											name="ein"
											value={values.ein}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>

										<TextField
											label="Doing Business As"
											placeholder="Enter dba"
											name="dba"
											value={values.dba ?? ""}
											onChange={(value) => {
												handleChange(value)
											}}
											onBlur={handleBlur}
										/>

										<TextField
											label="State Of Incorporation"
											placeholder="Enter State Of Incorporation"
											name="stateOfIncorporation"
											value={values.stateOfIncorporation}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<Dropdown
											name="businessVertical"
											label="Business Vertical"
											value={values.businessVertical}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.businessVertical ? <React.Fragment /> : <option value="" />}
											{Object.entries(BusinessVertical).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<TextField
											label="Year Of Incorporation"
											placeholder="Enter Year Of Incorporation"
											name="yearOfIncorporation"
											value={values.yearOfIncorporation ?? ""}
											onChange={handleChange}
											onBlur={handleBlur}
										/>

										<Dropdown
											name="numberOfEmployees"
											label="Number Of Employees"
											value={values.numberOfEmployees}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.numberOfEmployees ? <React.Fragment /> : <option value="" />}
											{Object.entries(BusinessNumberOfEmployees).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="annualRevenue"
											label="Annual Revenue"
											value={values.annualRevenue}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.annualRevenue ? <React.Fragment /> : <option value="" />}
											{Object.entries(BusinessAnnualRevenue).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="cashFlow"
											label="Cash Flow"
											value={values.cashFlow}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.cashFlow ? <React.Fragment /> : <option value="" />}
											{Object.entries(CashFlow).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="industry"
											label="Industry"
											value={values.industry}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{businessApplication.industry ? <React.Fragment /> : <option value="" />}
											{Object.entries(IndustryType).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<div className="countries-website-row">
											<CountriesOfOperationFormField
												value={values.countriesOfOperation}
												onChange={(val) => setFieldValue("countriesOfOperation", val)}
											/>
											<div className={"website"}>
												<TextField
													label="Website"
													placeholder="Enter website"
													name="website"
													value={values.website ?? ""}
													onChange={(value) => {
														handleChange(value)
													}}
													onBlur={handleBlur}
													disabled={values.website === null}
												/>
												<CheckboxField
													name="noWebsite"
													label="This business has no website"
													checked={values.website === null}
													onBlur={handleBlur}
													onChange={(value) => {
														const isChecked = value.target.checked
														if (isChecked) {
															setFieldValue("website", null, true)
														} else {
															setFieldValue("website", businessApplication.website ?? "", true)
														}
													}}
												/>
											</div>
										</div>

										<PhoneNumber
											name="phoneNumber"
											label="Phone Number"
											countryCodeName="phone.countryCode"
											phoneNumberName="phone.number"
											countryCodeValue={values.phone.countryCode}
											phoneNumberValue={values.phone?.number}
											countryCodeOnChange={(value) => {
												setFieldValue("phone.countryCode", value)
											}}
											phoneNumberOnChange={(value) => {
												setFieldValue("phone.number", value)
											}}
											countryCodeOnBlur={handleBlur}
											phoneNumberOnBlur={handleBlur}
											infoTooltipText="Enter the country code followed by the phone number."
										/>
									</>
								}
								right={
									<>
										<AddressComponent
											border
											header="Legal Business Address"
											headerTooltip="The address that is listed on your business's official registration documents."
											closed={false}
											namePrefix="address."
											street={values.address.street}
											street2={values.address.street2}
											city={values.address.city}
											country={values.address.country || ""}
											state={values.address.state}
											postalCode={values.address.postalCode}
											isUsOnlyAddress={true}
											handleChange={handleChange}
											handleBlur={handleBlur}
											setFieldValue={setFieldValue}
										/>
										<AddressComponent
											border
											header="Physical Operating Address"
											headerTooltip="The office or residential address where you conduct your daily work."
											closed={false}
											namePrefix="operatingAddress."
											street={values.operatingAddress.street}
											street2={values.operatingAddress.street2}
											city={values.operatingAddress.city}
											country={values.operatingAddress.country}
											state={values.operatingAddress.state}
											postalCode={values.operatingAddress.postalCode}
											isUsOnlyAddress={false}
											handleChange={(e: any) => {
												handleOperatingAddressInteraction()
												handleChange(e)
											}}
											handleBlur={handleBlur}
											setFieldValue={(field: string, value: any, shouldValidate?: boolean) => {
												handleOperatingAddressInteraction()
												setFieldValue(field, value, shouldValidate)
											}}
											error={shouldShowAddressError}
											errorMessage={operatingAddressError}
										/>
									</>
								}
							/>
						</form>
					)
				}}
			</Formik>
		</Card>
	)
}
